import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { useTestFlight } from "@/context/TestFlightContext";
import { getVenueDirectory } from "@/lib/api";
import LaunchBanner from "@/components/LaunchBanner";
import { motion } from "framer-motion";

const CONSUMER_LINKS = [
  { label: "How it works", to: "/how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "For venues", to: "/venues" },
  { label: "For events", to: "/events" },
  { label: "About", to: "/about" },
  { label: "DFW Venues", to: "/venues/directory" },
  { label: "Blog", to: "/blog" },
];

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
    <img
      src="/brand/logo-256.png"
      alt="GateMate logo"
      width="36"
      height="36"
      className="w-9 h-9 rounded-xl transition-transform group-hover:scale-105 shadow-[0_0_20px_rgba(0,198,174,0.35)]"
    />
    <span className="font-display text-xl font-bold tracking-tight">GateMate</span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const { openTestFlight } = useTestFlight();
  const location = useLocation();

  // "DFW Venues" stays out of the nav until at least one partner venue is
  // live — the directory page itself (and its footer link) remain reachable.
  const [venuesLive, setVenuesLive] = useState(() => {
    try {
      const cached = sessionStorage.getItem("gm_venues_count");
      return cached === null ? null : parseInt(cached, 10);
    } catch (_) {
      return null;
    }
  });

  useEffect(() => {
    getVenueDirectory()
      .then((d) => {
        const n = Array.isArray(d) ? d.length : 0;
        try { sessionStorage.setItem("gm_venues_count", String(n)); } catch (_) {}
        setVenuesLive(n);
      })
      .catch(() => setVenuesLive(0));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) =>
    location.pathname === to ||
    (to === "/venues" && location.pathname === "/for-venues");
  // One consistent header sitewide — the current page (e.g. For venues) is
  // highlighted via isActive. The directory link appears once venues exist.
  const links = CONSUMER_LINKS.filter((l) => l.to !== "/venues/directory" || (venuesLive || 0) > 0);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
    >
      <LaunchBanner />
      <div
        className={`transition-all duration-300 ${
          scrolled ? "backdrop-blur-2xl bg-black/60 border-b border-white/5" : "bg-transparent"
        }`}
      >
        <nav className="container-x flex items-center justify-between h-[72px]">
          <Logo />
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={`text-sm transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-neon-cyan after:transition-all ${
                  isActive(l.to)
                    ? "text-white after:w-full"
                    : "text-zinc-300 hover:text-white after:w-0 hover:after:w-full"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://app.gatemate.net/sign-in"
              data-testid="nav-open-workspace"
              className="text-xs text-zinc-500 hover:text-neon-cyan transition-colors px-3 py-1.5"
            >
              Sign in
            </a>
            {/* Venue partner login lives in the footer — the header stays
                attendee-first. */}
            <Button
              onClick={openTestFlight}
              data-testid="nav-waitlist-button"
              className="rounded-full bg-white text-black hover:bg-white/90 font-semibold px-5 btn-glow"
            >
              Get GateMate
            </Button>
          </div>

          {/* Mobile */}
          <div className="md:hidden">
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
              <SheetTrigger asChild>
                <button
                  data-testid="nav-mobile-toggle"
                  aria-label={openMobile ? "Close menu" : "Open menu"}
                  className="p-2 text-white"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0c1714] border-l border-white/10 text-white w-[80%]">
                <SheetTitle className="sr-only">GateMate navigation</SheetTitle>
                <SheetDescription className="sr-only">
                  Navigate GateMate. Includes links to how it works, pricing, for venues, for events, about, DFW venues, blog, partner log in, and get the app.
                </SheetDescription>
                <div className="flex flex-col gap-6 mt-10">
                  {links.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpenMobile(false)}
                      data-testid={`nav-mobile-link-${l.to.replace(/\//g, "") || "home"}`}
                      className="text-left text-lg font-medium text-zinc-200"
                    >
                      {l.label}
                    </Link>
                  ))}
                  <a
                    href="https://app.gatemate.net/sign-in"
                    onClick={() => setOpenMobile(false)}
                    data-testid="nav-mobile-open-workspace"
                    className="text-left text-lg font-medium text-neon-cyan border-t border-white/10 pt-4"
                  >
                    Sign in →
                  </a>
                  {/* Venue partner login lives in the footer. */}
                  <Button
                    onClick={() => { setOpenMobile(false); openTestFlight(); }}
                    data-testid="nav-mobile-waitlist-button"
                    className="rounded-full bg-gradient-to-r from-neon-violet to-neon-cyan text-white font-semibold mt-4"
                  >
                    Get GateMate
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>

    </motion.header>
  );
}
